import React, { useEffect, useState, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box, Paper, Typography, TextField, Button, Grid, Divider,
  FormControlLabel, Checkbox, IconButton, Table, TableHead,
  TableRow, TableCell, TableBody, TableContainer,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import SaveIcon from '@mui/icons-material/Save';
import { useVentaMovimientoStore } from '../store/ventaStore';
import type { VentaCreateRequest, VentaDetalleCreateRequest } from '../types/venta.types';
import { initialVentaCreate } from '../types/venta.types';

const emptyDetalle: VentaDetalleCreateRequest = {
  idArticulo: 0,
  descripcion: '',
  cantidad: 1,
  precioUnitario: 0,
  afectaStock: true,
};

export const VentaFormPage: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const isEdit = !!id;
  const { ventaDetalle, saving, fetchVenta, createVenta, updateVenta, clearVenta } = useVentaMovimientoStore();

  const [formData, setFormData] = useState<VentaCreateRequest>({ ...initialVentaCreate });

  useEffect(() => {
    if (isEdit && id) {
      fetchVenta(parseInt(id));
    }
    return () => clearVenta();
  }, [id]);

  useEffect(() => {
    if (isEdit && ventaDetalle) {
      const f = ventaDetalle.factura;
      setFormData({
        idComprobante: f.idMovimiento || 0,
        idPersona: 0,
        serie: f.serie || '',
        incluyeIGV: f.incluyeIGV ?? true,
        porcentajeIGV: f.porcentajeIGV ?? 18,
        direccionEntrega: f.direccionEntrega || '',
        guiaRemision: f.guiaRemision || '',
        ordenCompra: f.ordenCompra || '',
        observaciones: '',
        detalles: ventaDetalle.detalles.map((d) => ({
          idArticulo: d.idArticulo || 0,
          descripcion: d.descripcion || '',
          cantidad: d.cantidad,
          precioUnitario: d.precioUnitario,
          afectaStock: d.afectaStock,
        })),
      });
    }
  }, [ventaDetalle]);

  const handleChange = (field: keyof VentaCreateRequest, value: any) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleDetalleChange = (index: number, field: keyof VentaDetalleCreateRequest, value: any) => {
    setFormData((prev) => {
      const detalles = [...prev.detalles];
      detalles[index] = { ...detalles[index], [field]: value };
      return { ...prev, detalles };
    });
  };

  const addDetalle = () => {
    setFormData((prev) => ({
      ...prev,
      detalles: [...prev.detalles, { ...emptyDetalle }],
    }));
  };

  const removeDetalle = (index: number) => {
    setFormData((prev) => ({
      ...prev,
      detalles: prev.detalles.filter((_, i) => i !== index),
    }));
  };

  const igvRate = (formData.porcentajeIGV ?? 18) / 100;

  const totales = useMemo(() => {
    let subTotal = 0;
    let totalDescuento = 0;
    formData.detalles.forEach((d) => {
      const linea = d.cantidad * d.precioUnitario - (d.descuento ?? 0);
      subTotal += linea;
      totalDescuento += d.descuento ?? 0;
    });
    const totalIGV = subTotal * igvRate;
    const total = subTotal + totalIGV;
    return { subTotal, totalIGV, totalDescuento, total };
  }, [formData.detalles, igvRate]);

  const handleSubmit = async () => {
    try {
      if (isEdit && id) {
        await updateVenta(parseInt(id), formData);
      } else {
        await createVenta(formData);
      }
      navigate('/ventas/explorador');
    } catch {
      // Error handled by store
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
        <IconButton onClick={() => navigate('/ventas/explorador')}><ArrowBackIcon /></IconButton>
        <Typography variant="h5" fontWeight="bold">
          {isEdit ? 'Editar Venta' : 'Nueva Venta'}
        </Typography>
      </Box>

      <Paper sx={{ p: 3, mb: 3 }}>
        <Typography variant="h6" gutterBottom>Datos Generales</Typography>
        <Grid container spacing={2}>
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <TextField label="Serie" size="small" fullWidth value={formData.serie} onChange={(e) => handleChange('serie', e.target.value)} />
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <TextField label="ID Comprobante" size="small" fullWidth type="number" value={formData.idComprobante || ''} onChange={(e) => handleChange('idComprobante', parseInt(e.target.value) || 0)} />
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <TextField label="ID Cliente" size="small" fullWidth type="number" value={formData.idPersona || ''} onChange={(e) => handleChange('idPersona', parseInt(e.target.value) || 0)} />
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <TextField label="ID Vendedor" size="small" fullWidth type="number" value={formData.idVendedor || ''} onChange={(e) => handleChange('idVendedor', parseInt(e.target.value) || null)} />
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <FormControlLabel
              control={<Checkbox checked={formData.incluyeIGV} onChange={(e) => handleChange('incluyeIGV', e.target.checked)} />}
              label="Incluye IGV"
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <TextField label="% IGV" size="small" fullWidth type="number" value={formData.porcentajeIGV ?? 18} onChange={(e) => handleChange('porcentajeIGV', parseFloat(e.target.value) || 18)} />
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <TextField label="Dirección Entrega" size="small" fullWidth value={formData.direccionEntrega || ''} onChange={(e) => handleChange('direccionEntrega', e.target.value)} />
          </Grid>
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <TextField label="Guía Remisión" size="small" fullWidth value={formData.guiaRemision || ''} onChange={(e) => handleChange('guiaRemision', e.target.value)} />
          </Grid>
          <Grid size={{ xs: 12, sm: 12, md: 6 }}>
            <TextField label="Observaciones" size="small" fullWidth multiline rows={2} value={formData.observaciones || ''} onChange={(e) => handleChange('observaciones', e.target.value)} />
          </Grid>
        </Grid>
      </Paper>

      <Paper sx={{ p: 3, mb: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h6">Detalle</Typography>
          <Button variant="outlined" startIcon={<AddIcon />} onClick={addDetalle} size="small">Agregar Línea</Button>
        </Box>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell width={50}>#</TableCell>
                <TableCell>Descripción</TableCell>
                <TableCell width={100}>Cantidad</TableCell>
                <TableCell width={120}>P. Unitario</TableCell>
                <TableCell width={100}>Descuento</TableCell>
                <TableCell width={120}>SubTotal</TableCell>
                <TableCell width={50}></TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {formData.detalles.map((det, idx) => {
                const lineSubTotal = det.cantidad * det.precioUnitario - (det.descuento ?? 0);
                return (
                  <TableRow key={idx}>
                    <TableCell>{idx + 1}</TableCell>
                    <TableCell>
                      <TextField size="small" fullWidth value={det.descripcion} onChange={(e) => handleDetalleChange(idx, 'descripcion', e.target.value)} />
                    </TableCell>
                    <TableCell>
                      <TextField size="small" type="number" fullWidth value={det.cantidad} onChange={(e) => handleDetalleChange(idx, 'cantidad', parseFloat(e.target.value) || 0)} />
                    </TableCell>
                    <TableCell>
                      <TextField size="small" type="number" fullWidth value={det.precioUnitario} onChange={(e) => handleDetalleChange(idx, 'precioUnitario', parseFloat(e.target.value) || 0)} />
                    </TableCell>
                    <TableCell>
                      <TextField size="small" type="number" fullWidth value={det.descuento ?? 0} onChange={(e) => handleDetalleChange(idx, 'descuento', parseFloat(e.target.value) || 0)} />
                    </TableCell>
                    <TableCell align="right">S/ {lineSubTotal.toFixed(2)}</TableCell>
                    <TableCell>
                      <IconButton size="small" color="error" onClick={() => removeDetalle(idx)}><DeleteIcon fontSize="small" /></IconButton>
                    </TableCell>
                  </TableRow>
                );
              })}
              {formData.detalles.length === 0 && (
                <TableRow><TableCell colSpan={7} align="center" sx={{ py: 3, color: 'text.secondary' }}>Sin líneas de detalle. Haga clic en "Agregar Línea".</TableCell></TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>

        <Divider sx={{ my: 2 }} />

        <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
          <Box sx={{ minWidth: 250 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
              <Typography variant="body2">Sub Total:</Typography>
              <Typography variant="body2">S/ {totales.subTotal.toFixed(2)}</Typography>
            </Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
              <Typography variant="body2">Descuento:</Typography>
              <Typography variant="body2">S/ {totales.totalDescuento.toFixed(2)}</Typography>
            </Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
              <Typography variant="body2">IGV ({formData.porcentajeIGV}%):</Typography>
              <Typography variant="body2">S/ {totales.totalIGV.toFixed(2)}</Typography>
            </Box>
            <Divider sx={{ my: 1 }} />
            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
              <Typography variant="h6">Total:</Typography>
              <Typography variant="h6">S/ {totales.total.toFixed(2)}</Typography>
            </Box>
          </Box>
        </Box>
      </Paper>

      <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
        <Button variant="outlined" onClick={() => navigate('/ventas/explorador')}>Cancelar</Button>
        <Button variant="contained" startIcon={<SaveIcon />} onClick={handleSubmit} disabled={saving}>
          {saving ? 'Guardando...' : 'Guardar Venta'}
        </Button>
      </Box>
    </Box>
  );
};
